<script lang="ts" setup>
import type { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import { computed, ref, onMounted, type Ref } from 'vue';
import { errToStr } from '@/helpers/tools';
import { AccessControl, AC_MODES } from '@/models/AccessControl';
import { instance as api } from '@/helpers/api';
import {
    createValidator,
    createValidators,
    validateNotEmpty,
    validateEmail
} from '@kp/geofy-vuejs-lib/tools/model_validators.js';

const emit = defineEmits<{
    (e: 'created', value: AccessControl): void;
    (e: 'canceled'): void;
}>();

const props = defineProps<{
    geschaeft: Planungsgeschaeft;
}>();

const loading: Ref<boolean> = ref(false);
const acObjectRef: Ref<AccessControl | null> = ref(null);
const error: Ref<string | null> = ref(null);

const { allValid, validators, resetAll } = createValidators({
    mode: createValidator(
        computed(() => acObjectRef.value?.mode),
        [validateNotEmpty],
        { initialValidate: false }
    ),
    email: createValidator(
        computed(() => acObjectRef.value?.email || ''),
        [validateNotEmpty, validateEmail],
        { initialValidate: false }
    )
});

const formValid = computed(() => {
    return acObjectRef.value?.mode && acObjectRef.value?.email && allValid;
});

async function onSubmit() {
    loading.value = true;
    error.value = null;
    try {
        let res = await api().post('/AccessControl/create', {
            planning_permission_application_id: props.geschaeft.id,
            access_right: acObjectRef.value?.mode,
            email: acObjectRef.value?.email,
            comment: acObjectRef.value?.comment,
            access_type: 'person_organisation'
        });
        let resultObj = new AccessControl();
        resultObj.set(res.data);
        emit('created', resultObj);
    } catch (e) {
        console.error(e);
        error.value = errToStr(e);
    } finally {
        loading.value = false;
    }
}

onMounted(() => {
    let ac = new AccessControl();
    ac.mode = AC_MODES.MODE_READ;
    acObjectRef.value = ac;
    resetAll();
});

function onCancel() {
    emit('canceled');
}

function mutateRecord(field: string, value: any): void {
    if (acObjectRef.value) {
        acObjectRef.value.set(field, value);
    }
}
</script>

<template>
    <form @submit.prevent="onSubmit">
        <fieldset class="ktg-fieldset p-4rem">
            <h2 class="ktg-form-modal-title">
                {{ $t('berechtigung_erteilen') }}
            </h2>

            <ktg-label class="ktg-inner-box-padding">{{ $t('schreibrechte') }} *</ktg-label>
            <div class="flex-row">
                <ktg-labelled-choice class="ktg-inner-box-padding mt-0_5rem">
                    <ktg-radio
                        :checked="acObjectRef?.mode === AC_MODES.MODE_READ"
                        :value="AC_MODES.MODE_READ"
                        input-id="ac_mode_read"
                        @change="mutateRecord('mode', AC_MODES.MODE_READ)"
                    ></ktg-radio>
                    <ktg-label for="ac_mode_read">{{ $t('nein_nur_lesen') }}</ktg-label>
                </ktg-labelled-choice>
                <ktg-labelled-choice class="ktg-inner-box-padding mt-0_5rem">
                    <ktg-radio
                        :checked="acObjectRef?.mode === AC_MODES.MODE_READ_WRITE"
                        :value="AC_MODES.MODE_READ_WRITE"
                        input-id="ac_mode_read_write"
                        @change="mutateRecord('mode', AC_MODES.MODE_READ_WRITE)"
                    ></ktg-radio>
                    <ktg-label for="ac_mode_read_write">{{ $t('ja_lesen_und_schreiben') }}</ktg-label>
                </ktg-labelled-choice>
            </div>

            <ktg-field class="flex-row pt-1rem">
                <ktg-label for="emailaddress">{{ $t('emailaddress') + ' *' }}</ktg-label>
                <ktg-text-input
                    class="w-full"
                    :value="acObjectRef?.email"
                    input-id="emailaddress"
                    type="text"
                    :required="true"
                    error-id="err_email"
                    placeholder="andrea.muster@thurgau.ch"
                    :error="!validators.email.isValid.value"
                    @blur="validators.email.validate()"
                    @change="mutateRecord('email', $event.target.value)"
                />
                <ktg-field-error v-if="!validators.email.isValid.value" id="err_email">{{
                    validators.email.msg.value
                }}</ktg-field-error>
            </ktg-field>

            <ktg-field>
                <ktg-label for="msg">{{ $t('body') }} *</ktg-label>
                <ktg-text-area
                    :value="acObjectRef?.comment"
                    input-id="msg"
                    class="w-full"
                    :placeholder="$t('der_berechtigung_eine_nachricht_hinzufuegen')"
                    :maxlength="1000"
                    rows="10"
                    @change="mutateRecord('comment', $event.target.value)"
                ></ktg-text-area>
            </ktg-field>

            <ktg-alert-banner v-if="error" appearance="danger" class="my-1_5rem">
                {{ error }}
            </ktg-alert-banner>

            <div class="btn-container">
                <ktg-button appearance="secondary" :disabled="loading" @click="onCancel" class="col">
                    {{ $t('cancel') }}
                </ktg-button>
                <ktg-button
                    appearance="primary"
                    class="col"
                    :loading="loading"
                    :disabled="loading || !formValid"
                    type="submit"
                    trailing-icon="check"
                >
                    {{ $t('berechtigung_erteilen') }}
                </ktg-button>
            </div>
        </fieldset>
    </form>
</template>

<style lang="scss" scoped>
.btn-container {
    display: flex;
    gap: 1rem;
    flex-direction: row;
    ktg-button {
        flex: 1 1 0%;
    }
}
</style>
